import {Component, HostListener, Inject, Input, OnDestroy, OnInit} from '@angular/core';
import {ActivatedRoute, Router, UrlSegment} from "@angular/router";
import {EVENT_DETAILS} from "src/app/app-routing.module";
import {environment} from "@environment";
import {DOCUMENT} from "@angular/common";
import {AbstractComponent} from "@components";
import {
  DEFAULT_PAGE_SIZE,
  EventFilter,
  EventItem,
  EventItemType,
  FindEventsRequest,
  ListSortOrder,
  Lookups,
  Message, Paging,
  Sport, Waypoint
} from "@model";
import {EventService, LoaderService} from "@services";
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {debounceTime, distinctUntilChanged} from 'rxjs';

@Component({
  selector: 'app-event-calendar',
  templateUrl: './event-calendar.component.html',
  styleUrls: ['./event-calendar.component.scss']
})
export class EventCalendarComponent extends AbstractComponent implements OnInit, OnDestroy, Paging<EventItem> {
  @Input() countries: Lookups[] = [];
  @Input() sports: Sport[] = [];
  @Input() listOfEvents: EventItem[] = [];
  @Input({required: true}) languages: Lookups[] = [];
  @Input() favoriteItems: EventItem[] = [];
  @Input() unreadMessages: Message[] | undefined;

  calendarType: EventItemType = EventItemType.UPCOMING;
  EventItemType = EventItemType;
  name: string = "";
  dateTo: NgbDate | undefined;
  dateFrom: NgbDate | undefined;
  selectedLength: number | undefined;
  lengths: number[] = EventFilter.LENGTHS;
  page: number = 1;
  hasMoreEvents: boolean = false;
  images: string[] = [1, 2, 3].map((n: number): string => `${environment.assetsImagesPath}/assets/img/carousel/carousel${n}.png`);
  environment: any = environment;
  showFabButton: boolean = false;
  sportId: number | undefined;
  initialDate: NgbDate;
  queryParamsFirstChange: boolean = true;
  isLoading: boolean = false;
  eventMapVisible: boolean = false;
  lat: number = environment.headquartersLat;
  lng: number = environment.headquartersLng;
  waypointList: Waypoint[] = [];

  constructor(private router: Router, private eventService: EventService, private route: ActivatedRoute,
              @Inject(DOCUMENT) private document: Document, private loaderService: LoaderService) {
    super();
    const now: Date = new Date();
    this.initialDate = new NgbDate(now.getFullYear(), now.getMonth() + 1, now.getDate());
    //LeafletMapsComponent.zoom = 8;
  }

  @HostListener('window:scroll')
  toggleFabButton() {
    this.showFabButton = window.pageYOffset != 0;
  }

  ngOnInit(): void {
    this.subscribeToRouteDataChange();
    this.handleExploreResult(this.listOfEvents);
  }

  findWayPointList(): void {
    let wayPoints: Waypoint[] = [];
    if ((this.listOfEvents?.length ?? 0) > 0) {
      for (let event of this.listOfEvents) {
        if (event.latitude && event.longitude) {
          let waypoint: Waypoint = new Waypoint();
          waypoint.latitude = event.latitude;
          waypoint.longitude = event.longitude;
          waypoint.city = event.city;
          waypoint.country = event.country;
          waypoint.pointNameEn = event.nameEng;
          waypoint.pointNameHr = event.name;
          waypoint.pointNameIt = event.nameIta;
          waypoint.pointNameSlo = event.nameSlo;
          waypoint.eventItem = event;
          wayPoints.push(waypoint);
        }
      }
    }
    this.waypointList = wayPoints;
  }

  private subscribeToRouteDataChange() {
    this.subscriptions.push(this.route.url.subscribe((urlSegments: UrlSegment[]) => {
      const currentUrl: string = urlSegments.map((segment: UrlSegment) => segment.path).join('/');
      if (currentUrl.includes("finishedEvents")) {
        this.calendarType = EventItemType.FINISHED;
      } else if (currentUrl.includes("eventCalendar")) {
        this.calendarType = EventItemType.UPCOMING;
      }
    }));

    this.subscriptions.push(this.route.queryParams.pipe(debounceTime(environment.fullTextSearchDelay),
      distinctUntilChanged()).subscribe((queryParams: any) => {
      this.name = queryParams.name ? queryParams.name : "";
      if (this.calendarType === EventItemType.UPCOMING) {
        this.dateFrom = this.getNgbDateFromString(queryParams.dateFrom) ?? this.initialDate;
        this.dateTo = this.getNgbDateFromString(queryParams.dateTo);
      }
      if (this.calendarType === EventItemType.FINISHED) {
        this.dateTo = this.getNgbDateFromString(queryParams.dateTo) ?? this.initialDate;
        this.dateFrom = this.getNgbDateFromString(queryParams.dateFrom);
      }
      this.sportId = this.getSportValue(queryParams);
      this.selectedLength = this.getLengthValue(queryParams);
      this.eventMapVisible = this.getEventMapVisible(queryParams);
      if (!this.queryParamsFirstChange && this.isDateValid(queryParams.dateFrom) && this.isDateValid(queryParams.dateTo) && this.isLengthValid(queryParams.length) && this.isSportValid(queryParams.sport)) {
        this.onExplore(false);
      }
      this.queryParamsFirstChange = false;
    }))
  }

  private changeQueryParams(): void {
    let queryParams: { [key: string]: any } = {};
    switch (this.calendarType) {
      case EventItemType.FINISHED:
        if (this.dateFrom) {
          queryParams['dateFrom'] = this.dateFrom.year + "-" + this.dateFrom.month + "-" + this.dateFrom.day
        }
        if (this.dateTo && !this.dateTo.equals(this.initialDate)) {
          queryParams['dateTo'] = this.dateTo.year + "-" + this.dateTo.month + "-" + this.dateTo.day
        }
        break;
      case EventItemType.UPCOMING:
        if (this.dateFrom && !this.dateFrom.equals(this.initialDate)) {
          queryParams['dateFrom'] = this.dateFrom.year + "-" + this.dateFrom.month + "-" + this.dateFrom.day
        }
        if (this.dateTo) {
          queryParams['dateTo'] = this.dateTo.year + "-" + this.dateTo.month + "-" + this.dateTo.day
        }
        break;
      default:
        break;
    }

    if (this.selectedLength) {
      queryParams['length'] = this.selectedLength;
    }
    if (this.sportId) {
      queryParams['sport'] = this.sportId;
    }
    if (this.name && this.name.length > 0) {
      queryParams['name'] = this.name
    }
    queryParams['eventMapVisible'] = this.eventMapVisible
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: queryParams,
      replaceUrl: true,
    });
  }

  createFilter(): FindEventsRequest {
    return FindEventsRequest.createEventPageFilter(
      this.page,
      {
        name: this.name !== "" ? this.name : undefined,
        length: this.selectedLength ?? undefined,
        sport: this.sportId ?? undefined,
        ...this.getEventFilterDates()
      },
      this.calendarType === EventItemType.FINISHED ? ListSortOrder.ASC : ListSortOrder.DESC);
  }

  getEventFilterDates() {
    switch (this.calendarType) {
      case EventItemType.FINISHED:
        return {
          dateFrom: this.dateFrom ? this.getDateFromNgbDate(this.dateFrom)?.getTime() : undefined,
          dateTo: this.getDateFromNgbDate(this.dateTo ?? this.initialDate)?.getTime(),
        }
      case EventItemType.UPCOMING:
        return {
          dateTo: this.dateTo ? this.getDateFromNgbDate(this.dateTo)?.getTime() : undefined,
          dateFrom: this.getDateFromNgbDate(this.dateFrom ?? this.initialDate)?.getTime(),
        }
      default:
        return {
          dateFrom: undefined,
          dateTo: undefined,
        }
    }
  }

  getDateFromNgbDate(ngbDate: NgbDate) {
    let date = new Date();
    date.setFullYear(ngbDate.year);
    date.setMonth(ngbDate.month - 1)
    date.setDate(ngbDate.day)
    date.setHours(0, 0, 0, 0)
    return date;
  }

  handleExploreResult(listOfEvents: EventItem[], loadMore?: boolean): void {
    /// we don't use paging on upcoming events
    if (listOfEvents && this.calendarType !== EventItemType.UPCOMING) {
      if (listOfEvents?.length > DEFAULT_PAGE_SIZE) {
        this.hasMoreEvents = true;
        listOfEvents = (listOfEvents!.slice(0, DEFAULT_PAGE_SIZE))
      } else {
        this.hasMoreEvents = false;
      }
      if (loadMore && this.listOfEvents) {
        listOfEvents = this.listOfEvents?.concat(listOfEvents)
      }
    }
    this.setListOfItems(listOfEvents);
  }

  setListOfItems(listOfEvents: EventItem[]): void {
    if (listOfEvents != null && listOfEvents.length > 0) {
      this.listOfEvents = listOfEvents;
      this.page++;
    } else {
      this.listOfEvents = [];
    }
    if (this.calendarType === EventItemType.UPCOMING) {
      this.findWayPointList();
    }
  }

  onExplore(loadMore?: boolean) {
    if (loadMore !== true) {
      this.page = 1;
    }
    switch (this.calendarType) {
      case EventItemType.UPCOMING:
        if (loadMore === true) {
          this.loaderService.startLoading();
        } else {
          this.isLoading = true;
        }
        this.eventService.findEventCalendarList(this.createFilter()).subscribe((listOfEvents: EventItem[]) => {
            if (loadMore === true) {
              this.loaderService.dismissLoading();
            } else {
              setTimeout(() => {
                this.isLoading = false;
              }, 700);
            }
            this.handleExploreResult(listOfEvents, loadMore);
          }
        );
        break;
      case EventItemType.FINISHED:
        if (loadMore === true) {
          this.loaderService.startLoading();
        } else {
          this.isLoading = true;
        }
        this.eventService.filterFinishedEventsList(this.createFilter()).subscribe((listOfEvents: EventItem[]) => {
            if (loadMore === true) {
              this.loaderService.dismissLoading();
            } else {
              setTimeout(() => {
                this.isLoading = false;
              }, 700);
            }
            this.handleExploreResult(listOfEvents, loadMore);
          }
        );
        break;
      default:
        this.handleExploreResult([], false);
        break;
    }
  }

  loadMore() :void {
    this.onExplore(true);
  }

  isDateValid(dateValue: string | undefined) {
    if (!dateValue) return true;
    else {
      const date: Date = new Date(dateValue);
      return (!isNaN(date.getTime()))
    }
  }

  isSportValid(sportValue: string | undefined) {
    if (!sportValue) return true;
    else {
      return this.sports.filter((sport: Sport) => sport.id === +sportValue).length > 0 ?? false;
    }
  }

  isLengthValid(lengthValue: string | undefined) {
    if (!lengthValue) return true;
    else {
      return this.lengths.filter((length: number) => length === +lengthValue).length > 0 ?? false;
    }
  }

  getLengthValue(queryParams: any) {
    if (queryParams.length) {
      return this.isLengthValid(queryParams.length) ? +queryParams.length : undefined
    }
    return undefined;
  }

  getSportValue(queryParams: any) {
    if (queryParams.sport) {
      return this.isSportValid(queryParams.sport) ? +queryParams.sport : undefined
    }
    return undefined;
  }

  getEventMapVisible(queryParams: any) {
    if (queryParams.eventMapVisible) {
      return queryParams.eventMapVisible === "true";
    }
    return false;
  }

  getNgbDateFromString(dateString: string | undefined) {
    if (dateString) {
      const date = new Date(dateString);
      if (!isNaN(date.getTime())) {
        const ngbDate: NgbDate = new NgbDate(date.getFullYear(), date.getMonth() + 1, date.getDate());
        return new NgbDate(ngbDate.year, ngbDate.month, ngbDate.day)
      }
      return undefined;
    }
    return undefined;
  }

  dateChanged(value: { from: NgbDate | undefined, to: NgbDate | undefined }) {
    this.dateFrom = value.from;
    this.dateTo = value.to;
    this.changeQueryParams();
  }

  onLengthSelect(length?: number) {
    if (this.selectedLength === length || !length) {
      this.selectedLength = undefined;
    } else {
      this.selectedLength = length;
    }
    this.changeQueryParams()
  }


  onSportSelect(sportId?: number) {
    if (this.sportId === sportId || !sportId) {
      this.sportId = undefined;
    } else {
      this.sportId = sportId;
    }
    this.changeQueryParams()
  }

  handleInput(value: string) {
    this.name = value.trim() ?? "";
    this.changeQueryParams()
  }

  goToEventDetailsPage(item: EventItem): void {
    this.router.navigate([EVENT_DETAILS], {
      relativeTo: this.route,
      queryParams: {eventId: item.id}
    });
  }

  trackByFn(index: number, item: EventItem) {
    return item.id;
  }

  scrollToTop(): void {
    return this.document.body.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      inline: 'start'
    });
  }

  toggleEventMapVisible(): void {
    this.eventMapVisible = !this.eventMapVisible;
    this.changeQueryParams();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    //LeafletMapsComponent.zoom = LeafletMapsComponent.ZOOM_DEFAULT_VALUE;
  }
}
